import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bookmark } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useSavedLeads } from '@/lib/savedLeads';

/**
 * SAVE / UNSAVE A LEAD. Outlined = not saved, filled = saved. The state flips
 * instantly (optimistic) and reverts — with a brief inline "retry" hint — if
 * the server refuses. Saving never touches purchase, credit or capacity data.
 * Signed-out visitors are sent to sign in (no dead click).
 *
 *  variant="overlay" — round glass button on a card's cover image
 *  variant="inline"  — labelled pill for the lead detail page
 */
interface Props {
  listingId: string;
  variant?: 'overlay' | 'inline';
  className?: string;
}

const SaveLeadButton: React.FC<Props> = ({ listingId, variant = 'overlay', className = '' }) => {
  const { lang, path } = useI18n();
  const es = lang === 'es';
  const navigate = useNavigate();
  const { userId, enabled, isSaved, toggle } = useSavedLeads();
  const [pop, setPop] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!enabled) return null; // homeowners browse read-only
  const saved = isSaved(listingId);
  const label = failed
    ? es ? 'Error — reintentar' : 'Failed — tap to retry'
    : saved ? (es ? 'Guardado' : 'Saved') : es ? 'Guardar lead' : 'Save lead';

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!userId) {
      navigate(path('/login'));
      return;
    }
    setFailed(false);
    setPop(true);
    window.setTimeout(() => setPop(false), 220);
    toggle(listingId).catch(() => {
      setFailed(true);
      window.setTimeout(() => setFailed(false), 2500);
    });
  };

  const icon = (
    <Bookmark
      className={`h-[18px] w-[18px] transition-transform duration-200 ${pop ? 'scale-125' : 'scale-100'} ${
        saved ? 'fill-current' : ''
      }`}
      aria-hidden
    />
  );

  if (variant === 'inline') {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={saved}
        aria-label={label}
        style={{ touchAction: 'manipulation' }}
        className={`inline-flex min-h-[44px] items-center gap-2 rounded-xl border px-4 text-sm font-bold transition-colors ${
          saved
            ? 'border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100'
            : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50'
        } ${className}`}
      >
        {icon}
        {label}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={saved}
      aria-label={label}
      title={label}
      style={{ touchAction: 'manipulation' }}
      className={`inline-flex h-11 w-11 items-center justify-center rounded-full shadow-md backdrop-blur transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300 ${
        saved ? 'bg-brand-orange text-white hover:bg-orange-600' : 'bg-white/90 text-slate-700 hover:bg-white hover:text-brand-orange'
      } ${className}`}
    >
      {icon}
    </button>
  );
};

export default SaveLeadButton;
